import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, ShoppingBag, Film, Plus, Trash2, DollarSign, Pencil, Settings, Image as ImageIcon, Store, Ticket, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api, { formatApiError, mediaUrl } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { UploadButton } from "../components/cms/UploadButton";
import { MediaLibrary } from "../components/admin/MediaLibrary";
import { CollectionManager } from "../components/admin/CollectionManager";

const TIERS = ["silver", "gold", "diamond"];
const CATEGORIES = ["Tai Chi Kung", "Breathwork", "Yoga", "Assessment Tool", "Recipe/Remedy", "Reflection", "Recommendation Hub", "Seasonal Guidance", "Free Guide", "Free Ebook"];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [content, setContent] = useState([]);
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);

  const loadAll = async () => {
    const [s, m, b, o, c, p, ev] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/members"), api.get("/admin/bookings"), api.get("/admin/orders"), api.get("/content"),
      api.get("/products"), api.get("/events"),
    ]);
    setStats(s.data); setMembers(m.data); setBookings(b.data); setOrders(o.data); setContent(c.data);
    setProducts(p.data); setEvents(ev.data);
  };
  useEffect(() => { loadAll().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);

  const setTier = async (id, tier) => {
    await api.patch(`/admin/members/${id}/tier`, { tier });
    toast.success("Member tier updated.");
    loadAll();
  };

  const TABS = [
    ["overview", "Overview", LayoutDashboard],
    ["site", "Site Editor", Settings],
    ["media", "Media Library", ImageIcon],
    ["content", "Videos & Resources", Film],
    ["shop", "Shop Products", Store],
    ["events", "Events", Ticket],
    ["members", "Members", Users],
    ["bookings", "Bookings", CalendarDays],
    ["orders", "Orders", ShoppingBag],
  ];

  return (
    <Layout>
      <div className="section-pad !py-10 max-w-7xl mx-auto">
        <h1 className="font-display text-4xl font-semibold text-brand-ink">Admin Panel</h1>
        <p className="text-brand-muted text-sm">Edit pages, upload videos & images, manage the shop, events, members, bookings and orders — no code required.</p>

        <div className="mt-6 flex gap-2 border-b border-brand-line overflow-x-auto no-scrollbar">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px flex items-center gap-2 ${tab === k ? "border-brand-sage text-brand-sage" : "border-transparent text-brand-muted hover:text-brand-ink"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="admin-overview">
              <Stat label="Members" value={stats.members} />
              <Stat label="Revenue" value={`$${stats.revenue}`} icon={DollarSign} />
              <Stat label="Orders" value={stats.orders} />
              <Stat label="Bookings" value={stats.bookings} />
              <Stat label="Silver" value={stats.by_tier.silver} />
              <Stat label="Gold" value={stats.by_tier.gold} />
              <Stat label="Diamond" value={stats.by_tier.diamond} />
            </div>
          )}

          {tab === "site" && <SiteEditor />}

          {tab === "content" && <ContentManager content={content} reload={loadAll} />}

          {tab === "media" && <MediaLibrary />}

          {tab === "shop" && (
            <CollectionManager title="Product" endpoint="products" items={products} reload={loadAll} testid="admin-products"
              empty={{ name: "", category: "Books", kind: "product", price: 0, price_label: "", image: "", description: "", url: "", purchasable: true }}
              fields={[["name", "Product name"], ["category", "Category", "select", PRODUCT_CATS], ["price", "Price (USD)", "number"],
                ["price_label", "Price label (optional, e.g. \"$25\" or \"Contact for pricing\")"], ["image", "Image URL"],
                ["description", "Description", "area"], ["url", "More info link (optional)"], ["purchasable", "Can be added to cart", "bool"]]}
              rowTitle={(p) => p.name} rowSub={(p) => `${p.category} · ${p.price_label || "$" + p.price}`} />
          )}

          {tab === "events" && (
            <CollectionManager title="Event" endpoint="events" items={events} reload={loadAll} testid="admin-events"
              empty={{ title: "", format: "online", date: "", price: 0, image: "", description: "" }}
              fields={[["title", "Event title"], ["format", "Format", "select", ["online", "in-person"]], ["date", "Date & time", "datetime-local"],
                ["price", "Price (USD)", "number"], ["image", "Image URL"], ["description", "Description", "area"]]}
              rowTitle={(e) => e.title} rowSub={(e) => `${e.format} · ${(e.date || "").replace("T", " ").slice(0, 16)} · $${e.price}`} />
          )}

          {tab === "members" && (
            <div className="card-surface overflow-x-auto" data-testid="admin-member-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-brand-muted border-b border-brand-line">
                  <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Joined</th><th className="p-3">Consults</th><th className="p-3">Tier</th>
                </tr></thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-brand-line/60">
                      <td className="p-3 text-brand-ink">{m.name}</td>
                      <td className="p-3 text-brand-inksoft">{m.email}</td>
                      <td className="p-3 text-brand-muted">{(m.created_at || "").slice(0, 10)}</td>
                      <td className="p-3 text-brand-muted">{m.consultations_used}/{m.allotment}</td>
                      <td className="p-3">
                        <select value={m.tier} onChange={(e) => setTier(m.id, e.target.value)} className="border rounded px-2 py-1" data-testid={`member-tier-${m.id}`} disabled={m.role === "admin"}>
                          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "bookings" && (
            <div className="card-surface overflow-x-auto" data-testid="admin-bookings">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-brand-muted border-b border-brand-line">
                  <th className="p-3">Member</th><th className="p-3">Service</th><th className="p-3">Date</th><th className="p-3">Status</th>
                </tr></thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-brand-line/60">
                      <td className="p-3 text-brand-ink">{b.user_name}<br /><span className="text-xs text-brand-muted">{b.user_email}</span></td>
                      <td className="p-3">{b.service}</td>
                      <td className="p-3 text-brand-muted">{b.date} {b.time}</td>
                      <td className="p-3"><span className={`pill ${b.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-brand-cream2 text-brand-sage"}`}>{b.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "orders" && (
            <div className="card-surface overflow-x-auto" data-testid="admin-orders">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-brand-muted border-b border-brand-line">
                  <th className="p-3">Type</th><th className="p-3">Email</th><th className="p-3">Detail</th><th className="p-3">Amount</th><th className="p-3">Status</th>
                </tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-b border-brand-line/60">
                      <td className="p-3 text-brand-ink capitalize">{o.type}</td>
                      <td className="p-3 text-brand-inksoft">{o.email}</td>
                      <td className="p-3 text-brand-muted">{o.tier || o.event_title || (o.items ? `${o.items.length} item(s)` : "-")}</td>
                      <td className="p-3">${o.amount}</td>
                      <td className="p-3"><span className="pill bg-brand-cream2 text-brand-tan">{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card-surface p-6">
      <p className="text-sm text-brand-muted">{label}</p>
      <p className="font-display text-4xl font-semibold text-brand-ink mt-1">{value}</p>
    </div>
  );
}

const PRODUCT_CATS = ["Memberships", "Books", "Videos & Audio", "Oil Blends", "Vision Training", "Retreats & Gifts", "Trainings & Courses"];

const PAGES = [
  ["/", "Homepage"], ["/packages", "Membership Packages"], ["/zenergize-holistic-health-club", "Club Page"],
  ["/shop", "Shop"], ["/events", "Events & Retreats"], ["/trainings", "Trainings"],
];

const SITE_FIELDS = [
  ["hero_badge", "Hero badge text", "text"],
  ["hero_title", "Hero title", "text"],
  ["hero_subtitle", "Hero subtitle", "area"],
  ["hero_video_id", "Hero Vimeo video ID", "text"],
  ["hero_cta_primary", "Hero primary button", "text"],
  ["hero_cta_secondary", "Hero secondary button", "text"],
  ["pricing_heading", "Pricing section heading", "text"],
  ["pricing_subtext", "Pricing section subtext", "area"],
  ["regenerate_heading", "'Reawaken' section heading", "text"],
  ["regenerate_body", "'Reawaken' section body", "area"],
  ["astrology_url", "Chinese Astrology link URL", "text"],
];

function SiteEditor() {
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);

  const { setEditMode } = useSite();
  useEffect(() => { api.get("/settings").then((r) => setVals(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const values = Object.fromEntries(SITE_FIELDS.map(([k]) => [k, vals[k] ?? ""]));
      await api.put("/admin/settings", { values });
      toast.success("Homepage updated — changes are live.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!vals) return <p className="text-brand-muted">Loading…</p>;

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
    <div className="card-surface p-6" data-testid="admin-visual-editor">
      <h3 className="font-display text-2xl font-semibold text-brand-ink">Visual Page Editor</h3>
      <p className="text-sm text-brand-muted mt-1 mb-5">Open any page in edit mode, then click on any outlined text to rewrite it, or use the buttons on images and the hero video to replace them. Everything saves instantly.</p>
      <div className="space-y-2">
        {PAGES.map(([path, label]) => (
          <Link key={path} to={path} onClick={() => setEditMode(true)} data-testid={`visual-edit-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="flex items-center justify-between rounded-xl border border-brand-line px-4 py-3 hover:border-brand-sage hover:bg-brand-cream2 transition-colors">
            <span className="font-medium text-brand-ink">{label}</span>
            <span className="text-sm text-brand-sage flex items-center gap-1">Edit visually <ExternalLink size={14} /></span>
          </Link>
        ))}
      </div>
      <p className="text-xs text-brand-muted mt-4">Tip: the floating “Edit This Page” button (bottom-left) works on every page while you are logged in as admin.</p>
    </div>
    <div className="card-surface p-6" data-testid="admin-site-editor">
      <h3 className="font-display text-2xl font-semibold text-brand-ink">Homepage Text & Media</h3>
      <p className="text-sm text-brand-muted mt-1 mb-5">Edit any homepage wording, the hero video ID, or the astrology link. Changes save instantly, no code needed.</p>
      <div className="space-y-4">
        {SITE_FIELDS.map(([k, label, type]) => (
          <div key={k}>
            <label className="text-sm font-medium text-brand-ink">{label}</label>
            {type === "area" ? (
              <textarea rows={2} className="input-field mt-1" value={vals[k] || ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} data-testid={`site-${k}`} />
            ) : (
              <input className="input-field mt-1" value={vals[k] || ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} data-testid={`site-${k}`} />
            )}
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-primary mt-6" data-testid="site-save">{saving ? "Saving…" : "Save Changes"}</button>
    </div>
    </div>
  );
}

function ContentManager({ content, reload }) {
  const empty = { type: "video", category: "Tai Chi Kung", title: "", source_url: "", embed_url: "", required_tier: "silver", thumbnail: "", description: "" };
  const [form, setForm] = useState(empty);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/content", form);
      toast.success(form.id ? "Content updated." : "Content added.");
      setForm(empty); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const edit = (c) => { setForm({ id: c.id, type: c.type, category: c.category, title: c.title, source_url: c.source_url || "", embed_url: c.embed_url || "", required_tier: c.required_tier, thumbnail: c.thumbnail || "", description: c.description || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { await api.delete(`/admin/content/${id}`); toast.success("Deleted."); reload(); };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={save} className="card-surface p-6 space-y-3 lg:col-span-1 h-fit" data-testid="admin-content-form">
        <h3 className="font-semibold text-brand-ink flex items-center gap-2"><Plus size={16} /> {form.id ? `Edit Content (${form.id})` : "Add Content"}</h3>
        <input required placeholder="Title" className="input-field" value={form.title} onChange={upd("title")} data-testid="content-title" />
        <div className="grid grid-cols-2 gap-2">
          <select className="input-field" value={form.type} onChange={upd("type")} data-testid="content-type"><option value="video">Video</option><option value="resource">Resource</option></select>
          <select className="input-field" value={form.required_tier} onChange={upd("required_tier")} data-testid="content-tier">{TIERS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </div>
        <select className="input-field" value={form.category} onChange={upd("category")}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        <input placeholder="Source URL (WordPress link)" className="input-field" value={form.source_url} onChange={upd("source_url")} />
        <input placeholder="Video: Vimeo URL or uploaded video link" className="input-field" value={form.embed_url} onChange={upd("embed_url")} data-testid="content-embed" />
        <UploadButton accept="video/*" label="Upload video file" testid="content-upload-video"
          onUploaded={(m) => setForm((f) => ({ ...f, embed_url: m.url, type: "video" }))} />
        <input placeholder="Thumbnail URL" className="input-field" value={form.thumbnail} onChange={upd("thumbnail")} data-testid="content-thumb" />
        <div className="flex items-center gap-3">
          {form.thumbnail && <img src={mediaUrl(form.thumbnail)} alt="" className="w-16 h-10 rounded object-cover border" />}
          <UploadButton accept="image/*" isPublic label="Upload thumbnail" testid="content-upload-thumb"
            onUploaded={(m) => setForm((f) => ({ ...f, thumbnail: m.url }))} />
        </div>
        <textarea placeholder="Description" rows={2} className="input-field" value={form.description} onChange={upd("description")} />
        <button className="btn-primary w-full" data-testid="content-save">{form.id ? "Update Content" : "Save Content"}</button>
        {form.id && <button type="button" onClick={() => setForm(empty)} className="w-full text-sm text-brand-muted mt-1">Cancel edit</button>}
      </form>

      <div className="lg:col-span-2 card-surface overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-brand-muted border-b border-brand-line">
            <th className="p-3">Title</th><th className="p-3">Type</th><th className="p-3">Category</th><th className="p-3">Tier</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {content.map((c) => (
              <tr key={c.id} className="border-b border-brand-line/60">
                <td className="p-3 text-brand-ink">{c.title}</td>
                <td className="p-3 text-brand-muted capitalize">{c.type}</td>
                <td className="p-3 text-brand-muted">{c.category}</td>
                <td className="p-3"><span className="pill bg-brand-cream2 text-brand-sage capitalize">{c.required_tier}</span></td>
                <td className="p-3 whitespace-nowrap">
                  <button onClick={() => edit(c)} className="text-brand-sage mr-3" data-testid={`edit-content-${c.id}`}><Pencil size={15} /></button>
                  <button onClick={() => del(c.id)} className="text-red-400" data-testid={`delete-content-${c.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
